import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    SimpleChanges
} from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

import { combineLatest, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { isEqual } from 'lodash';

import { InputValidationModel } from '../../models/input-validation.model';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { IncludeExcludeModel } from '../../../Misc/models/include-exclude-model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { IncludeOrExclude } from '../../../Dialogs/services/data-select-dialog.service.interface';
import { AutocompleteChipsOptions } from '../AutocompleteChips/autocomplete-chips.component';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';

@Component({
    selector: 'agpr-include-exclude-chips',
    templateUrl: './include.exclude.chips.component.html',
    styleUrls: ['./include.exclude.chips.component.scss'],
    providers: [getProvider(IncludeExcludeChipsComponent)],
    animations: [
        trigger('containerState', [
            transition(':enter', [
                style({ opacity: 0, height: 0 }),
                animate('250ms ease-in', style({ opacity: 1, height: '*' }))
            ]),
            transition(':leave', [
                style({ opacity: 1, height: '*' }),
                animate('250ms ease-in', style({ opacity: 0, height: 0 }))
            ])
        ]),
        trigger('labelState', [
            state('visible', style({
                opacity: 1,
                flex: 1,
                width: '2.813rem',
                minWidth: '2.813rem',
                maxWidth: '2.813rem'
            })),
            state('hidden', style({
                opacity: 0,
                flex: 0,
                width: 0
            })),
            transition('hidden <=> visible', animate('250ms ease-in'))
        ])
    ],
    standalone: false
})
export class IncludeExcludeChipsComponent<T> extends ControlValueAccessorModel<IncludeExcludeModel<T>> implements OnInit, OnChanges {
    readonly agprComponentStyle = AgprComponentStyleEnum;

    readonly browseAllButtonSpec = {
        icon: 'fa-regular fa-duotone fa-binoculars',
        tooltip: 'Monitoring.search.browseAll',
    };
    readonly includeOrExclude = IncludeOrExclude;
    readonly buttonTypes = ButtonTypesEnum;

    @Input() id: string = null;
    @Input() splitOnPattern: RegExp = null;
    @Input() trimQuotesOnNewChips: boolean = true;
    @Input() disabled: boolean = false;
    @Input() tabindex: number = null;
    @Input() max: number = null;
    @Input() headerLabel: string = null;
    @Input() placeholder: string = '';
    @Input() styleClass: string = null;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.social;
    @Input() itemTooltipPosition: string;


    // what's the name of the property on the object T which we want to display on the chips
    // leave unset if the chips themselves are just strings
    @Input() labelLookupField: keyof T = null;

    // the key you want to to pass to agpr-dropdown-chips as labelLookupKey
    @Input() keyLookupField: keyof T = null;

    // passed down to whatever chip input component we use, to validate entries
    @Input() chipValidations: InputValidationModel[] = [];

    @Input() includeLabel: string = 'include';
    @Input() excludeLabel: string = 'exclude';

    // Tooltip for the component itself which is shown with an 'i' icon next to the label
    @Input() tooltip: string = null;

    @Input() caretTooltip: string = 'Discover.targeting.searchFieldLabels.expandButton.tooltip';

    // The general path for tooltip of each item in the drop downs. eg: Discover.targeting.definition.media.
    // This value will be concat with the enum value for each of the items in the drop down to create the tooltip
    @Input() tooltipPath: string = null;

    @Input() expanded: boolean = false;
    @Input() showIncludeAll: boolean = true;

    // set either 'autocompleteOptions' (autocomplete mode), or 'optionsFetcher' (dropdown mode), or neither (text input mode)
    @Input() autocompleteOptions: AutocompleteChipsOptions;
    @Input() optionsFetcher: () => Observable<T[]>;
    isAutoComplete: boolean = false;
    isDropdown: boolean = false;

    // sometimes, you want the include exclude model to be made of ids, but still able to fetch the objects they represent
    // in order to populate the chips.
    // you can:
    // - set the name of the id property (usually just 'id') as 'idField',
    // - provide a function as 'lookupById' to fetch the objects by the relevant property in order to populate the chips
    // - bind [(idModel)] (and '(idModelChange)' if you care about when it changes)
    // in this case, you shouldn't also use ngModel.
    @Input() idField: keyof T = null;
    @Input() lookupById: (ids: (keyof T)[]) => Observable<T[]> = null;
    @Input() idModel: IncludeExcludeModel<keyof T>;
    @Input() ariaDescribedBy: string;

    @Output() idModelChange: EventEmitter<IncludeExcludeModel<keyof T>> = new EventEmitter();
    @Output() blur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() browseAll: EventEmitter<IncludeOrExclude> = new EventEmitter<IncludeOrExclude>();
    showBrowseAll: boolean;

    ngOnInit(): void {
        if (!this.value) {
            this.value = new IncludeExcludeModel<T>();
        } else {
            this.checkShouldExpand();
        }

        this.isAutoComplete = !!this.autocompleteOptions;
        this.isDropdown = !!this.optionsFetcher;

        if (this.idModel) {
            if (!(this.lookupById && this.idField)) {
                console.error('bound an idModel without also binding lookupById or idField');
            }
        }

        this.showBrowseAll = this.browseAll.observers.length > 0;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.hasOwnProperty('idModel')) {
            // if we have bound 'idModel', and detect changes from the parent, we should fetch the objects associated
            // with the ids in order to populate the chips.
            if (this.showIncludeAll) {
                combineLatest([
                    this.lookupById(this.idModel.includeAll),
                    this.lookupById(this.idModel.include),
                    this.lookupById(this.idModel.exclude),
                ]).pipe(take(1)).subscribe(([includeAll, include, exclude]) => {
                    const tempValue = new IncludeExcludeModel({
                        includeAll: includeAll,
                        include: include,
                        exclude: exclude
                    });

                    if (!isEqual(this.value, tempValue)) {
                        this.value = tempValue;
                    }
                });
            } else {
                combineLatest([
                    this.lookupById(this.idModel.include),
                    this.lookupById(this.idModel.exclude),
                ]).pipe(take(1)).subscribe(([include, exclude]) => {
                    const tempValue = new IncludeExcludeModel({
                        includeAll: [],
                        include: include,
                        exclude: exclude
                    });

                    if (!isEqual(this.value, tempValue)) {
                        this.value = tempValue;
                    }

                    this.checkShouldExpand();
                });
            }
        }
    }

    // since we moved the forms to formControlName instead of Inputs.
    // we need to checkShouldExpand when we write to the component.
    // checkShouldExpand is used when editing social topics
    writeValue(value: any): void {
        super.writeValue(value);
        this.checkShouldExpand();
    }

    handleChanges(property: IncludeOrExclude, value: T[]): void {
        this.value[property] = value;
        this.onModelChange(this.value);

        if (this.idModel) {
            this.idModel = this.value.toSubModel(this.idField);
            this.idModelChange.emit(this.idModel);
        }

        this.checkShouldExpand();
    }

    checkShouldExpand(): void {
        if (this.value && (this.value.exclude.length > 0 || this.value.includeAll?.length > 0)) {
            this.expanded = true;
        }
    }

    toggleExpanded(): void {
        // Closes any panels that are open before toggling
        document.body.click();
        this.expanded = !this.expanded;
    }

    onBlur(event: FocusEvent) {
        this.onModelTouched();
        this.blur.emit(event);
    }
}
